
import { Options, Vue } from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import session, { sessionStore } from '@/shared/session';
import api from '@/shared/api';

@Options({
    props: {},
})
export default class TopBar extends Vue {
    currentMode = this.getCurrentMode();
    billingType = process.env.VUE_APP_RAFFISH_BILLING_TYPE || '';

    creditsLoading = false;
    creditsAvailable: number | null = null;
    creditsLoadError = false;

    get hasBusinessUnit(): boolean {
        return !!session.getCurrentBusinessUnit();
    }

    get creditsBalanceLabel(): string {
        if (this.creditsLoadError || this.creditsAvailable === null) {
            return 'Credits';
        }
        return `${new Intl.NumberFormat().format(this.creditsAvailable)} credits`;
    }

    async created() {
        await this.loadCreditsBalance();
    }

    async loadCreditsBalance() {
        if (!session.getCurrentBusinessUnit()) {
            this.creditsAvailable = null;
            return;
        }

        this.creditsLoading = true;
        this.creditsLoadError = false;
        try {
            const overview = await api.getCreditsOverview();
            this.creditsAvailable = overview.available;
        } catch (e) {
            this.creditsAvailable = null;
            this.creditsLoadError = true;
            console.error(e);
        } finally {
            this.creditsLoading = false;
        }
    }

    getCurrentMode() {
        return document.querySelector("html")?.dataset?.bsTheme;
    }

    toggleMode(mode: string) {
        const html = document.querySelector("html");
        if (html) {
            html.dataset.bsTheme = mode;
            this.currentMode = mode;
        }
    }

    /** Tracks session reloads so subscription banner reacts after upgrade. */
    get businessUnitsRevision(): number {
        return sessionStore.businessUnitsRevision;
    }

    get currentBusinessUnit() {
        void this.businessUnitsRevision;
        const businessUnits = session.getBusinessUnits();
        const currentBusinessUnitId = session.getCurrentBusinessUnit();
        return businessUnits.find((bu) => bu.id === currentBusinessUnitId);
    }

    get showSubscriptionBanner(): boolean {
        const businessUnit = this.currentBusinessUnit;
        if (!businessUnit && this.billingType !== 'business-unit') {
            return false;
        }

        if (this.billingType === 'business-unit' && (!businessUnit || businessUnit.subscription === 'n/a')) {
            return true;
        }

        if (!businessUnit) {
            return false;
        }

        if (businessUnit.subscription === 'trial') {
            return true;
        }

        if (businessUnit.subscription_end) {
            const endDate = new Date(businessUnit.subscription_end);
            if (!isNaN(endDate.getTime())) {
                const daysUntilEnd = (endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
                return daysUntilEnd <= 30;
            }
        }

        return false;
    }

    get subscriptionBannerText(): string {
        const businessUnit = this.currentBusinessUnit;
        if (this.billingType === 'business-unit' && (!businessUnit || businessUnit.subscription === 'n/a')) {
            return 'No active subscription found for this business unit.';
        }
        if (businessUnit?.subscription === 'trial') {
            return 'You are currently using a trial subscription.';
        }
        return 'Your subscription is nearing its end date.';
    }

    get subscriptionBannerLinkText(): string {
        const businessUnit = this.currentBusinessUnit;
        if (this.billingType === 'business-unit' && (!businessUnit || businessUnit.subscription === 'n/a')) {
            return 'Start subscription';
        }
        if (businessUnit?.subscription === 'trial') {
            return 'Convert to paid subscription';
        }
        return 'Extend subscription';
    }

    get subscriptionEndText(): string {
        const businessUnit = this.currentBusinessUnit;
        if (this.billingType === 'business-unit' && (!businessUnit || businessUnit.subscription === 'n/a')) {
            return '';
        }
        if (!businessUnit?.subscription_end) {
            return '';
        }

        const endDate = new Date(businessUnit.subscription_end);
        if (isNaN(endDate.getTime())) {
            return '';
        }

        return `It ends on ${endDate.toLocaleDateString()}.`;
    }

    goToSubscriptionPage() {
        const businessUnit = this.currentBusinessUnit;
        const routeName = businessUnit?.subscription === 'trial' ? 'subscription-upgrade' : 'subscription-extend';
        this.$router.push({ name: routeName });
    }

    @Watch('$route')
    async onRouteChange() {
        await session.refreshBusinessUnits();
        await this.loadCreditsBalance();
    }
}
